import type { ApexOptions } from 'apexcharts'
import type {
  SectionId,
  SectionsComparison,
  SegmentComparisonItem
} from '../../../features/engagement/domain/models/sections'
import { buildGroupedBarChartOptions, type ChartSeriesItem } from '../../shared/charts'
import { formatDurationHours, getSegmentColors, SECTION_LABEL_KEYS, type TranslateFn } from './section-display'
import type { SectionsMetricKey } from './SectionsStackedChart.vm'

// Ordered union of the sections reported by any segment; excludes 'other' (spec §6, "(excl. Other)")
export const getComparisonSections = (comparison: SectionsComparison): SectionId[] => {
  const sections: SectionId[] = []

  for (const segment of comparison.segments) {
    for (const item of segment.items) {
      if (item.section !== 'other' && !sections.includes(item.section)) sections.push(item.section)
    }
  }

  return sections
}

const PER_USER_FIELDS: Record<SectionsMetricKey, keyof Omit<SegmentComparisonItem, 'section'>> = {
  seconds: 'secondsPerUser',
  visits: 'visitsPerUser',
  interactions: 'interactionsPerUser'
}

// Per-user counts are averages over a handful of visits, so one decimal carries
// the difference between tiers that rounding to whole numbers would flatten.
const formatPerUserCount = (value: number): string => (Number.isFinite(value) ? value.toFixed(1) : '0.0')

export const getComparisonValueFormatter = (metric: SectionsMetricKey): ((value: number) => string) =>
  metric === 'seconds' ? formatDurationHours : formatPerUserCount

// One series per segment (API order): the selected per-user metric per section
export const getSectionsComparisonChartOptions = (
  comparison: SectionsComparison,
  metric: SectionsMetricKey,
  t: TranslateFn
): ApexOptions => {
  const sections = getComparisonSections(comparison)
  const colors = getSegmentColors(comparison.segments)
  const field = PER_USER_FIELDS[metric]

  const series: ChartSeriesItem[] = comparison.segments.map((segment) => {
    const bySection = new Map(segment.items.map((item) => [item.section, item[field]]))

    return {
      name: segment.label,
      data: sections.map((section) => bySection.get(section) ?? 0),
      color: colors[segment.key]
    }
  })

  return buildGroupedBarChartOptions({
    categories: sections.map((section) => t(SECTION_LABEL_KEYS[section])),
    series,
    valueFormatter: getComparisonValueFormatter(metric)
  })
}
